import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
// import useGoogleAuth from "../../hooks/useGoogleAuth"; // Commented out - Google auth not ready
import { signUp, validateSession } from "../../api/authApi";

import InputLabel from "../Shared/InputLabel";
import Input from "../Shared/Input";
import Button from "./Button";
import CTA from "./CTA";
import FormWrapper from "./FormWrapper";
import AlertRed from "../Shared/AlertRed";
import PasswordStrengthIndicator from "../Shared/PasswordStrengthIndicator";
import { getPasswordValidationRules } from "../../utils/passwordValidation";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";

const StepIndicator = ({ currentStep }) => {
  const steps = [
    { number: 1, label: "Email" },
    { number: 2, label: "Profile" },
  ];

  return (
    <div className="flex items-center justify-center mb-4">
      {steps.map((step, index) => {
        const isCompleted = currentStep > step.number;
        const isActive = currentStep === step.number;
        return (
          <div key={step.number} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  isActive || isCompleted
                    ? "bg-accent-500 text-white"
                    : "bg-cream-300 text-ink-500"
                }`}
              >
                {isCompleted ? (
                  <CheckIcon className="size-4" />
                ) : (
                  step.number
                )}
              </div>
              <span
                className={`mt-1 text-xs font-medium ${
                  isActive || isCompleted ? "text-accent-600" : "text-ink-400"
                }`}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`w-12 h-0.5 mx-2 mb-5 ${
                  isCompleted ? "bg-accent-500" : "bg-cream-300"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

// Email Step Component
const EmailStep = ({ onSubmit, onBack, email, setEmail, error }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { email: email },
  });

  return (
    <div>
      <CTA
        question="Already a member?"
        linkText="Sign in to continue"
        page="signin"
      />
      <FormWrapper>
        <div className="mb-6">
          <StepIndicator currentStep={1} />
          <p className="text-ink-600 text-center">
            Enter your email to get started
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <InputLabel htmlFor="email">Email address</InputLabel>
            <Input
              id="email"
              name="email"
              autoFocus
              errormessage={errors?.email?.message}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                  message: "Invalid email address",
                },
              })}
            />
          </div>

          {error && (
            <AlertRed
              title="Email Already Exists"
              message={
                <>
                  This email is already associated with an account. You can{" "}
                  <Link
                    to={"/signin"}
                    className="font-semibold leading-6 text-accent-600 hover:text-accent-500"
                  >
                    Sign In
                  </Link>{" "}
                  or use a different email to create a new account.
                </>
              }
            />
          )}

          <div>
            <Button
              type="submit"
              className="w-full bg-accent-500 hover:bg-accent-400 focus-visible:outline-accent-500"
            >
              Continue
            </Button>
          </div>
        </form>

        {/* Commented out Google sign-up - not ready for production
        <div>
          <SeperatorWithText text="Or sign up with" />
          <SSOButtonWrapper>
            <GoogleSignInButton googleLoginHandler={onBack} />
          </SSOButtonWrapper>
        </div>
        */}
      </FormWrapper>
    </div>
  );
};

// Details Step Component
const DetailsStep = ({ email, onSubmit, onBack, error }) => {
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    values: { email },
  });

  const passwordValue = watch("password");

  return (
    <div>
      <CTA
        question="Already a member?"
        linkText="Sign in to continue"
        page="signin"
      />
      <FormWrapper>
        <div className="mb-6">
          <StepIndicator currentStep={2} />
          <h2 className="text-xl font-semibold text-ink-900 text-center mb-2">
            Complete your profile
          </h2>
          <p className="text-ink-600 text-center">
            Just a few more details to finish setting up your account
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <InputLabel htmlFor="firstName">First name</InputLabel>
            <Input
              id="firstName"
              name="firstName"
              autoFocus
              errormessage={errors?.firstName?.message}
              {...register("firstName", {
                onBlur: (e) => {
                  setValue("firstName", e.target.value.trim());
                },
                required: "First name is required",
              })}
            />
          </div>
          <div>
            <InputLabel htmlFor="lastName">Last name</InputLabel>
            <Input
              id="lastName"
              name="lastName"
              errormessage={errors?.lastName?.message}
              {...register("lastName", {
                onBlur: (e) => {
                  setValue("lastName", e.target.value.trim());
                },
                required: "Last name is required",
              })}
            />
          </div>
          <div>
            <InputLabel htmlFor="password">Password</InputLabel>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                errormessage={errors?.password?.message}
                {...register("password", getPasswordValidationRules())}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[0.85rem] text-ink-400 hover:text-ink-600"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeSlashIcon className="size-5" />
                ) : (
                  <EyeIcon className="size-5" />
                )}
              </button>
            </div>
            <PasswordStrengthIndicator password={passwordValue} />
          </div>

          {error && (
            <AlertRed
              title="Sign-Up Error"
              message={
                "We're sorry, but something went wrong during sign-up. Please try again later."
              }
            />
          )}

          <div className="flex space-x-3">
            <Button
              type="button"
              onClick={onBack}
              disabled={isSubmitting}
              className="flex-1 bg-ink-200 hover:bg-ink-300 text-ink-800 focus-visible:outline-ink-800"
            >
              Back
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-accent-500 hover:bg-accent-400 focus-visible:outline-accent-500"
            >
              {isSubmitting ? "Creating Account..." : "Create Account"}
            </Button>
          </div>

          <p className="text-xs text-center text-ink-500 mt-4">
            By creating an account, you agree to our{" "}
            <a
              href="https://anyspot.io/terms.html"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-accent-600 hover:text-accent-500"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="https://anyspot.io/privacy.html"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-accent-600 hover:text-accent-500"
            >
              Privacy Policy
            </a>
            .
          </p>
        </form>
      </FormWrapper>
    </div>
  );
};

const SignUp = () => {
  const [step, setStep] = useState("email"); // 'email' | 'details'
  const [email, setEmail] = useState("");
  const [alert, setAlert] = useState(null);
  const navigate = useNavigate();
  // const { googleLoginHandler } = useGoogleAuth(); // Commented out - Google auth not ready

  const handleEmailSubmit = (emailData) => {
    setAlert(null);
    setEmail(emailData.email);
    setStep("details");
  };

  const handleDetailsSubmit = async (data) => {
    try {
      await signUp(data);
      // Redirect to dashboard with welcome flag for first-time guidance
      navigate("/dashboard?welcome=true");
    } catch (error) {
      if (error?.response?.status === 400) {
        setAlert("email_exists");
        setStep("email"); // Go back to email step
      } else {
        setAlert("error");
      }
    }
  };

  const handleBackToEmail = () => {
    setStep("email");
    setAlert(null);
  };

  // const handleGoogleSignIn = () => {
  //   googleLoginHandler();
  // }; // Commented out - Google auth not ready

  // Check if user is already logged in and redirect to dashboard
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        await validateSession();
        // If session is valid, redirect to dashboard regardless of verification status
        navigate("/dashboard");
      } catch (error) {
        // No valid session, stay on signup page
      }
    };
    checkExistingSession();
  }, [navigate]);

  if (step === "email") {
    return (
      <EmailStep
        onSubmit={handleEmailSubmit}
        onBack={handleBackToEmail} // Changed from handleGoogleSignIn - Google auth not ready
        email={email}
        setEmail={setEmail}
        error={alert === "email_exists"}
      />
    );
  }

  return (
    <DetailsStep
      email={email}
      onSubmit={handleDetailsSubmit}
      onBack={handleBackToEmail}
      error={alert === "error"}
    />
  );
};

export default SignUp;
